import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { AuthError, confirmForgotPassword, forgotPassword } from '../../auth/cognito.js';
import { Button, Field, InlineError, TextInput } from '../../ui/index.js';
import { AuthLayout, AuthTitle } from './AuthLayout.js';
import { CodeInput } from './CodeInput.js';
import styles from './Auth.module.css';

/** Spec 01: Forgot submits to Sent. Sent offers a return to Sign in. */
export function Forgot() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email);
      navigate('/auth/sent', { state: { email: email.trim().toLowerCase() } });
    } catch (err) {
      // Never reveal whether an address exists.
      if (err instanceof AuthError && err.code === 'limit') setError(err.message);
      else navigate('/auth/sent', { state: { email: email.trim().toLowerCase() } });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <form className={styles.form} onSubmit={submit}>
        <AuthTitle title="Reset your password" description="Enter the email you use for Titan. If it has an account, a code arrives in a minute." />
        {error && <InlineError>{error}</InlineError>}
        <Field label="Email" htmlFor="email">
          <TextInput id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <Button type="submit" size="lg" full disabled={busy || !email.trim()}>
          Send reset code
        </Button>
        <div className={styles.links}>
          <Link to="/auth/sign-in">Back to sign in</Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function Sent() {
  const navigate = useNavigate();
  const state = (history.state?.usr ?? {}) as { email?: string };
  const [email] = useState(state.email ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmForgotPassword(email, code, password);
      navigate('/auth/sign-in', { replace: true });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <form className={styles.form} onSubmit={submit}>
        <AuthTitle
          title="Check your inbox"
          description={`If ${email || 'that address'} has a Titan account, it just received a six-digit code. Enter it with your new password.`}
        />
        {error && <InlineError>{error}</InlineError>}
        <CodeInput value={code} onChange={setCode} disabled={busy} />
        <Field label="New password" hint="At least 12 characters, with upper and lower case, a number and a symbol." htmlFor="password">
          <TextInput id="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <Button type="submit" size="lg" full disabled={busy || code.length !== 6 || password.length < 12 || !email}>
          Set new password
        </Button>
        <div className={styles.links}>
          <Link to="/auth/sign-in">Back to sign in</Link>
          <Link to="/auth/forgot">Send another code</Link>
        </div>
      </form>
    </AuthLayout>
  );
}
